import { imageByPath, type ImageAsset } from '@/assets';
import { foes, type Foe } from './foes';
import { SYLLABLES, word, type PracticeWord, type TargetSound } from './words';

/**
 * The Foe Elevator's floors: each is a foe whose codename the child uncovers by practising /r/
 * in one context, in the order speech therapists sequence /r/ practice. Every target has
 * clinician-rated recordings from 28-37 PERCEPT-R children. See docs/design/foe-elevator.md.
 */
export interface Floor {
  id: string;
  number: number;
  foe: Foe;
  /** Short label of the practised context, shown to children and parents. */
  focus: string;
  /** Card colour theme (CSS colour). */
  color: string;
  words: readonly PracticeWord[];
}

export const R_SOUND: TargetSound = { label: '/r/', phoneme: 'ɹ' };

/** Cue-card picture by name; a neutral placeholder until the art exists. */
function picture(name: string): ImageAsset {
  const asset =
    imageByPath[`objects/target-words/${name}`] ??
    imageByPath['objects/target-words/code-card'] ??
    imageByPath['objects/target-words/rocks'];
  if (!asset) throw new Error(`no picture for ${name}`);
  return asset;
}

type Syllable = (typeof SYLLABLES)[keyof typeof SYLLABLES];

/** A practice syllable as a target of its own (floor 1). */
function syllable(s: Syllable, ...pairs: string[]): PracticeWord {
  return { ...word(picture('code-card'), s, ...pairs), kind: 'syllable' };
}

const w = (name: string, s: Syllable, ...pairs: string[]) => word(picture(name), s, ...pairs);
const S = SYLLABLES;

export const floors: readonly Floor[] = [
  {
    id: 'cipher-cat',
    number: 1,
    foe: foes.cipherCat,
    focus: 'R warm-up',
    color: '#7b4fd6',
    words: [
      syllable(S.rah, 'r:ɹ', 'ah:ɑ'),
      syllable(S.ray, 'r:ɹ', 'ay:eɪ'),
      syllable(S.ree, 'r:ɹ', 'ee:i'),
      syllable(S.roh, 'r:ɹ', 'oh:oʊ'),
      syllable(S.roo, 'r:ɹ', 'oo:u'),
    ],
  },
  {
    id: 'agent-rook',
    number: 2,
    foe: foes.agentRook,
    focus: 'R at the start',
    color: '#2f9a5b',
    words: [
      w('rocks', S.rah, 'r:ɹ', 'o:ɑ', 'ck:k'),
      w('ring', S.ree, 'r:ɹ', 'i:ɪ', 'ng:ŋ'),
      w('rope', S.roh, 'r:ɹ', 'o:oʊ', 'pe:p'),
      w('rat', S.rah, 'r:ɹ', 'a:æ', 't:t'),
      w('rice', S.rah, 'r:ɹ', 'i:aɪ', 'ce:s'),
      w('rain', S.ray, 'r:ɹ', 'ai:eɪ', 'n:n'),
    ],
  },
  {
    id: 'shadow-fox',
    number: 3,
    foe: foes.shadowFox,
    focus: 'R in longer words',
    color: '#e0652b',
    words: [
      w('rocket', S.rah, 'r:ɹ', 'o:ɑ', 'ck:k', 'e:ɪ', 't:t'),
      w('robot', S.roh, 'r:ɹ', 'o:oʊ', 'b:b', 'o:ɑ', 't:t'),
      w('rabbit', S.rah, 'r:ɹ', 'a:æ', 'bb:b', 'i:ɪ', 't:t'),
      w('rhino', S.rah, 'rh:ɹ', 'i:aɪ', 'n:n', 'o:oʊ'),
      w('robin', S.rah, 'r:ɹ', 'o:ɑ', 'b:b', 'i:ɪ', 'n:n'),
      w('radio', S.ray, 'r:ɹ', 'a:eɪ', 'd:d', 'i:i', 'o:oʊ'),
    ],
  },
  {
    id: 'echo-lynx',
    number: 4,
    foe: foes.echoLynx,
    focus: 'R blends',
    color: '#1aa3a3',
    words: [
      w('train', S.ray, 't:t', 'r:ɹ', 'ai:eɪ', 'n:n'),
      w('frog', S.rah, 'f:f', 'r:ɹ', 'o:ɑ', 'g:ɡ'),
      w('crab', S.rah, 'c:k', 'r:ɹ', 'a:æ', 'b:b'),
      w('crown', S.rah, 'c:k', 'r:ɹ', 'ow:aʊ', 'n:n'),
      w('drum', S.rah, 'd:d', 'r:ɹ', 'u:ʌ', 'm:m'),
      w('grapes', S.ray, 'g:ɡ', 'r:ɹ', 'a:eɪ', 'p:p', 'es:s'),
    ],
  },
  {
    id: 'misty-mole',
    number: 5,
    foe: foes.mistyMole,
    focus: 'AR and OR',
    color: '#e08a1a',
    words: [
      w('star', S.ar, 's:s', 't:t', 'a:ɑ', 'r:ɹ'),
      w('guitar', S.ar, 'g:ɡ', 'ui:ɪ', 't:t', 'a:ɑ', 'r:ɹ'),
      w('popcorn', S.or, 'p:p', 'o:ɑ', 'p:p', 'c:k', 'o:ɔ', 'r:ɹ', 'n:n'),
      w('acorn', S.or, 'a:eɪ', 'c:k', 'o:ɔ', 'r:ɹ', 'n:n'),
      w('tornado', S.or, 't:t', 'o:ɔ', 'r:ɹ', 'n:n', 'a:eɪ', 'd:d', 'o:oʊ'),
      w('dinosaur', S.or, 'd:d', 'i:aɪ', 'n:n', 'o:ʌ', 's:s', 'au:ɔ', 'r:ɹ'),
    ],
  },
  {
    id: 'nova-owl',
    number: 6,
    foe: foes.novaOwl,
    focus: 'EAR, AIR, IRE',
    color: '#3f7fe0',
    words: [
      w('beard', S.ear, 'b:b', 'ea:ɪ', 'r:ɹ', 'd:d'),
      w('cheer', S.ear, 'ch:tʃ', 'ee:ɪ', 'r:ɹ'),
      w('square', S.air, 's:s', 'q:k', 'u:w', 'a:ɛ', 're:ɹ'),
      w('fire', S.ar, 'f:f', 'i:aɪ', 're:ɹ'),
      w('vampire', S.ar, 'v:v', 'a:æ', 'm:m', 'p:p', 'i:aɪ', 're:ɹ'),
      w('chandelier', S.ear, 'ch:ʃ', 'a:æ', 'n:n', 'd:d', 'e:ʌ', 'l:l', 'ie:ɪ', 'r:ɹ'),
    ],
  },
];

export const floorIds: readonly string[] = floors.map((f) => f.id);

export function floorById(id: string): Floor | undefined {
  return floors.find((f) => f.id === id);
}
